import React, { useEffect } from 'react';
import {
  DialogRoot,
  DialogContent,
  DialogBody,
  DialogHeading,
  DialogHeader,
  Separator,
  Stack,
  Text,
} from '@global-ecom/nitro-uds/elements';

import { ProductCartItem, WishListItem } from '__generated__/graphql';
import { useMiniCart } from 'hooks/useMiniCart';
import { useTranslate } from 'hooks/useTranslations';
import {
  GiftCardItem,
  MiniCartActions,
  ProductItem,
} from 'ui/components/MiniCart';
import { ProductRecommender } from 'ui/content/ProductRecommender';
import { useCart } from 'hooks/useCart';
import { useSiteConfig } from 'hooks/useSiteConfig';
import {
  isLongTermPreOrder,
  isLongTermPreOrderPaymentMethod,
} from 'utils/longTermPreOrder';
import { PaymentMethod } from 'utils/constants';
import { useModalsOpenStore } from 'store';

import { isTherePromosButNotASinglePromoClaimed } from '../GiftWithPurchase/utils';

export const MiniCart = () => {
  const { cart, cartResult } = useCart();
  const {
    staticFeatures: { enablePopularity },
    shoppingCartItemType,
  } = useSiteConfig();
  const { setModalOpen } = useModalsOpenStore();
  const {
    products,
    closeMiniCart,
    mixedCartNotAllowed,
    tryingToOpenMiniCartFromGWP,
  } = useMiniCart();
  const translate = useTranslate();

  // Make sure the miniCart open boolean in context is
  // set to false whenever the component is unmounted
  useEffect(() => () => closeMiniCart(), [closeMiniCart]);

  useEffect(() => {
    // if we not are trying to open the miniCart from GWP and have unclaimed products in the giftWithPurchases
    // true -> redirect to open GWPModal instead of miniCart

    if (
      !tryingToOpenMiniCartFromGWP &&
      isTherePromosButNotASinglePromoClaimed({ cart })
    ) {
      closeMiniCart();
      setModalOpen('GWPModal', true);
    }
    // false -> just open the miniCart as usual
  }, [cart, closeMiniCart, setModalOpen, tryingToOpenMiniCartFromGWP]);

  const addedToWishlist = !!products?.find(
    p => p.__typename === 'WishListItem'
  );

  const showAmazonPayButton =
    !cartResult.fetching &&
    !cartResult.error &&
    !mixedCartNotAllowed &&
    (!isLongTermPreOrder(cart) ||
      isLongTermPreOrderPaymentMethod(cart, PaymentMethod.AMAZON_PAY));

  const idsForRecommendations = products?.reduce((acc, curr) => {
    if ('product' in curr) {
      return [
        ...acc,
        (curr as WishListItem | ProductCartItem)?.product.masterId,
      ];
    }
  }, [] as any);

  const recommenderGridTemplateColumns =
    'repeat(@{itemCount}, calc(33.333% - 10px))';

  return (
    <>
      <DialogRoot
        defaultOpen
        size="sm"
        align="start"
        justify="end"
        dataTestId="minicart-added-to"
        onOpenChange={(open: boolean) =>
          open === false ? closeMiniCart() : null
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogHeading>
              {translate(
                addedToWishlist
                  ? 'miniWishListAddedToWishlist'
                  : 'miniCartAddedToCart'
              )}
            </DialogHeading>
          </DialogHeader>
          <DialogBody>
            <Stack className="overflow-y-auto max-h-[256px]" gap="sm">
              <Separator role="none" gap="none" />
              {products?.map((product, idx) => {
                if (
                  product.__typename === 'ProductCartItem' ||
                  addedToWishlist
                ) {
                  return (
                    <React.Fragment key={`product-item-${product.id}-${idx}`}>
                      <ProductItem
                        key={`product-${product.id}-${idx}`}
                        type={shoppingCartItemType}
                        product={product as ProductCartItem}
                      />

                      {enablePopularity &&
                        product.__typename === 'ProductCartItem' &&
                        !product.bonusItemId && // if the product is a gift with purchase item -> don't show the popularity message
                        product.popularityCount != null &&
                        product.popularityCount !== 0 && (
                          <div className="text-center mx-auto">
                            <Text weight="bold" size="xs" transform="uppercase">
                              {product.popularityCount}+{' '}
                              {translate('popularityMessage')}
                            </Text>
                          </div>
                        )}
                    </React.Fragment>
                  );
                }
                if (product.__typename === 'GiftCardCartItem') {
                  return (
                    <GiftCardItem
                      key={`giftcard-${product.id}-${idx}`}
                      amount={product.amount}
                      imageUrl={product.imageUrl}
                      recipientEmail={product.recipientEmail}
                      recipientName={product.recipientName}
                      senderName={product.senderName}
                      message={product.message}
                    />
                  );
                }
                return null;
              })}
            </Stack>
            <Separator role="none" gap="sm" />
            <MiniCartActions
              addedToWishlist={addedToWishlist}
              productCount={cart?.productCount}
              giftWithPurchases={cart?.giftWithPurchases}
              mixedCartNotAllowed={mixedCartNotAllowed}
              showAmazonPayButton={showAmazonPayButton}
              closeMiniCart={closeMiniCart}
            />
            <Separator gap="sm" />
            <ProductRecommender
              type="itemAddedPopup"
              ids={idsForRecommendations}
              displayRecommendations={!!products}
              gridTemplateColumnConfig={{
                mobile: recommenderGridTemplateColumns,
                tablet: recommenderGridTemplateColumns,
                desktop: recommenderGridTemplateColumns,
              }}
              stackProductInfo
              isMiniCart={true}
            />
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </>
  );
};
